import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCarrito } from "../lib/CarritoContext.jsx";
import { useAuth } from "../lib/useAuth";
import { enviarPedido } from "../lib/envios";
import { soles } from "../lib/format";
import { IGV, MODOS_ENTREGA } from "../lib/config";
import s from "./Comercio.module.css";

/**
 * Metodos de pago disponibles hoy. No se piden ni se almacenan datos de
 * tarjeta: el cobro se cierra por transferencia o con el equipo comercial.
 *
 * PENDIENTE: si se decide integrar pasarela (Culqi, Niubiz o Mercado Pago),
 * se agrega aqui la opcion y se redirige al checkout del proveedor. Los datos
 * de tarjeta siempre viajan al proveedor, nunca a esta web.
 */
const METODOS_PAGO = [
  {
    id: "transferencia",
    label: "Transferencia o deposito",
    detalle:
      "Te enviamos los datos bancarios al confirmar. El despacho se programa con el voucher.",
  },
  {
    id: "coordinar",
    label: "Coordinar con ventas",
    detalle:
      "Un asesor te contacta para cerrar condiciones, credito o factura a 30 dias.",
  },
];

const CLIENTE_VACIO = {
  nombre: "",
  email: "",
  telefono: "",
  empresa: "",
  ruc: "",
  direccion: "",
  referencia: "",
};

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, vaciar } = useCarrito();
  const { user, perfil } = useAuth();

  const [cliente, setCliente] = useState({
    ...CLIENTE_VACIO,
    nombre: perfil?.nombre || user?.displayName || "",
    email: perfil?.email || user?.email || "",
    telefono: perfil?.telefono || "",
    empresa: perfil?.empresa || "",
    ruc: perfil?.ruc || "",
  });
  const [modoEntrega, setModoEntrega] = useState("recojo");
  const [metodoPago, setMetodoPago] = useState("transferencia");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  if (items.length === 0) {
    return (
      <div className={`container ${s.vacio}`}>
        <p className="eyebrow">Checkout</p>
        <h1 className={s.vacioTitulo}>No hay nada que confirmar</h1>
        <p className={s.vacioTexto}>
          Tu carrito esta vacio. Agrega productos antes de continuar.
        </p>
        <Link to="/productos" className="btn btn--accent">
          Ir al catalogo
        </Link>
      </div>
    );
  }

  const entrega = MODOS_ENTREGA[modoEntrega];
  const costoEnvio = entrega.costo ?? 0;
  const igvFinal = (subtotal + costoEnvio) * IGV;
  const totalFinal = subtotal + costoEnvio + igvFinal;

  function actualizar(campo, valor) {
    setCliente((c) => ({ ...c, [campo]: valor }));
  }

  async function alEnviar(e) {
    e.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      const { codigo } = await enviarPedido({
        cliente,
        items,
        clienteUid: user?.uid || null,
        entrega: {
          modo: modoEntrega,
          label: entrega.label,
          costo: costoEnvio,
          direccion: modoEntrega === "recojo" ? "" : cliente.direccion,
          referencia: modoEntrega === "recojo" ? "" : cliente.referencia,
          metodoPago,
        },
      });
      vaciar();
      navigate(`/confirmacion/${codigo}?tipo=pedido`);
    } catch (err) {
      console.error(err);
      setError(
        "No pudimos registrar el pedido. Revisa tu conexion o escribenos por WhatsApp."
      );
      setEnviando(false);
    }
  }

  const requiereDireccion = modoEntrega !== "recojo";

  return (
    <div className={`container ${s.pagina}`}>
      <header className={s.cabeceraPagina}>
        <p className="eyebrow">Checkout</p>
        <h1 className={s.tituloPagina}>Confirmar pedido</h1>
      </header>

      <form className={s.layout} onSubmit={alEnviar}>
        <div className={s.formulario}>
          {/* --- Datos del cliente --- */}
          <fieldset className={s.bloque}>
            <legend className={s.bloqueTitulo}>
              <span className={s.bloqueNum}>01</span> Tus datos
            </legend>

            <div className={s.filaCampos}>
              <div className="field">
                <label className="label" htmlFor="nombre">
                  Nombre y apellido *
                </label>
                <input
                  id="nombre"
                  className="input"
                  required
                  value={cliente.nombre}
                  onChange={(e) => actualizar("nombre", e.target.value)}
                />
              </div>

              <div className="field">
                <label className="label" htmlFor="telefono">
                  Telefono *
                </label>
                <input
                  id="telefono"
                  className="input"
                  type="tel"
                  required
                  placeholder="999 999 999"
                  value={cliente.telefono}
                  onChange={(e) => actualizar("telefono", e.target.value)}
                />
              </div>
            </div>

            <div className="field">
              <label className="label" htmlFor="email">
                Correo *
              </label>
              <input
                id="email"
                className="input"
                type="email"
                required
                value={cliente.email}
                onChange={(e) => actualizar("email", e.target.value)}
              />
            </div>

            <div className={s.filaCampos}>
              <div className="field">
                <label className="label" htmlFor="empresa">
                  Empresa (opcional)
                </label>
                <input
                  id="empresa"
                  className="input"
                  value={cliente.empresa}
                  onChange={(e) => actualizar("empresa", e.target.value)}
                />
              </div>

              <div className="field">
                <label className="label" htmlFor="ruc">
                  RUC (para factura)
                </label>
                <input
                  id="ruc"
                  className="input"
                  inputMode="numeric"
                  maxLength={11}
                  value={cliente.ruc}
                  onChange={(e) => actualizar("ruc", e.target.value)}
                />
              </div>
            </div>
          </fieldset>

          {/* --- Entrega --- */}
          <fieldset className={s.bloque}>
            <legend className={s.bloqueTitulo}>
              <span className={s.bloqueNum}>02</span> Entrega
            </legend>

            <div className={s.opciones}>
              {Object.entries(MODOS_ENTREGA).map(([id, modo]) => (
                <label
                  key={id}
                  className={`${s.opcion} ${modoEntrega === id ? s.opcionActiva : ""}`}
                >
                  <input
                    type="radio"
                    name="entrega"
                    value={id}
                    checked={modoEntrega === id}
                    onChange={() => setModoEntrega(id)}
                  />
                  <span className={s.opcionTexto}>
                    <span className={s.opcionLabel}>{modo.label}</span>
                    <span className={s.opcionDetalle}>{modo.detalle}</span>
                  </span>
                  <span className={s.opcionCosto}>
                    {modo.costo === null
                      ? "Por cotizar"
                      : modo.costo === 0
                        ? "Sin costo"
                        : soles(modo.costo)}
                  </span>
                </label>
              ))}
            </div>

            {requiereDireccion && (
              <>
                <div className="field">
                  <label className="label" htmlFor="direccion">
                    Direccion de entrega *
                  </label>
                  <input
                    id="direccion"
                    className="input"
                    required
                    placeholder="Calle, numero, distrito"
                    value={cliente.direccion}
                    onChange={(e) => actualizar("direccion", e.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="label" htmlFor="referencia">
                    Referencia
                  </label>
                  <input
                    id="referencia"
                    className="input"
                    placeholder="Punto de descarga, horario permitido, contacto en obra"
                    value={cliente.referencia}
                    onChange={(e) => actualizar("referencia", e.target.value)}
                  />
                </div>
              </>
            )}
          </fieldset>

          {/* --- Pago --- */}
          <fieldset className={s.bloque}>
            <legend className={s.bloqueTitulo}>
              <span className={s.bloqueNum}>03</span> Pago
            </legend>

            <div className={s.opciones}>
              {METODOS_PAGO.map((m) => (
                <label
                  key={m.id}
                  className={`${s.opcion} ${metodoPago === m.id ? s.opcionActiva : ""}`}
                >
                  <input
                    type="radio"
                    name="pago"
                    value={m.id}
                    checked={metodoPago === m.id}
                    onChange={() => setMetodoPago(m.id)}
                  />
                  <span className={s.opcionTexto}>
                    <span className={s.opcionLabel}>{m.label}</span>
                    <span className={s.opcionDetalle}>{m.detalle}</span>
                  </span>
                </label>
              ))}
            </div>

            <p className={s.nota}>
              No pedimos datos de tarjeta en esta web. El pedido queda
              registrado y el cobro se cierra por transferencia o con tu asesor.
            </p>
          </fieldset>

          {error && <p className={s.mensajeError}>{error}</p>}
        </div>

        {/* --- Resumen --- */}
        <aside className={s.resumen}>
          <h2 className={s.resumenTitulo}>Tu pedido</h2>

          <div className={s.miniItems}>
            {items.map((i) => (
              <div key={i.id} className={s.miniItem}>
                <span className={s.miniItemNombre}>
                  {i.nombre}
                  <br />
                  <span className={s.miniItemCant}>
                    {i.cantidad} {i.unidad}
                  </span>
                </span>
                <span className="mono">{soles((i.precio ?? 0) * i.cantidad)}</span>
              </div>
            ))}
          </div>

          <dl className={s.resumenLista}>
            <div>
              <dt>Subtotal</dt>
              <dd className="mono">{soles(subtotal)}</dd>
            </div>
            <div>
              <dt>Envio</dt>
              <dd className="mono">
                {entrega.costo === null ? "Por cotizar" : soles(costoEnvio)}
              </dd>
            </div>
            <div>
              <dt>IGV ({Math.round(IGV * 100)}%)</dt>
              <dd className="mono">{soles(igvFinal)}</dd>
            </div>
            <div className={s.resumenTotal}>
              <dt>Total</dt>
              <dd className="mono">{soles(totalFinal)}</dd>
            </div>
          </dl>

          {entrega.costo === null && (
            <p className="hint">
              El flete a provincia se cotiza aparte y no esta incluido en el
              total.
            </p>
          )}

          <button
            type="submit"
            className="btn btn--accent btn--block"
            disabled={enviando}
          >
            {enviando ? "Registrando..." : "Confirmar pedido"}
          </button>

          <Link to="/carrito" className={s.seguir}>
            &larr; Volver al carrito
          </Link>
        </aside>
      </form>
    </div>
  );
}
